import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Map as MapIcon, List as ListIcon, X, Columns2, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { MapView } from '@/components/map/MapView';
import { PlacesSearchBar } from '@/components/map/PlacesSearchBar';
import { ListingCard } from '@/components/common/ListingCard';
import { SkeletonCard } from '@/components/common/SkeletonCard';
import { EmptyState } from '@/components/common/EmptyState';
import { AdSlot } from '@/components/common/AdSlot';
import { CompareHero } from '@/components/compare/CompareHero';
import { CompareTable } from '@/components/compare/CompareTable';
import { AppliedChips } from '@/components/filters/AppliedChips';
import { FilterControls } from '@/components/filters/FilterControls';
import { FilterSidebar } from '@/components/filters/FilterSidebar';
import { SortSelect } from '@/components/filters/SortSelect';
import { ListingTypeToggle, getListingType } from '@/components/filters/ListingTypeToggle';
import { cn } from '@/lib/utils';
import { getAllListings, getGeoBoundary, lookupListingByAddress } from '@/services/api';
import { paramsToFilters, filtersToParams, countActiveFilters } from '@/utils/filters';
import { FILTER_CODES } from '@/utils/statusModel';

function displayCount(items, total) {
  return `${(total ?? items.length).toLocaleString('en-US')} homes`;
}

function boundsEqual(a, b) {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.north - b.north) < 1e-6 &&
    Math.abs(a.south - b.south) < 1e-6 &&
    Math.abs(a.east - b.east) < 1e-6 &&
    Math.abs(a.west - b.west) < 1e-6
  );
}

export default function MapSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [activeId, setActiveId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMapListing, setSelectedMapListing] = useState(null);
  const [mobileView, setMobileView] = useState('map');
  const [listView, setListView] = useState('compare');
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  const [shellHeight, setShellHeight] = useState(() => {
    if (typeof window === 'undefined') return null;
    const vv = window.visualViewport?.height ?? window.innerHeight;
    return Math.max(200, vv - 96);
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [filtersDesktopOpen, setFiltersDesktopOpen] = useState(false);
  const [searchOnMove, setSearchOnMove] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [searchAreaBounds, setSearchAreaBounds] = useState(null);
  const [drawnPolygon, setDrawnPolygon] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const listRefs = useRef({});
  const mapRef = useRef(null);
  const fetchKeyRef = useRef(0);
  const lastBoundsRef = useRef(null);
  const boundsDebounceRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const numPagesRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const currentFetchParamsRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const update = () => {
      const vv = window.visualViewport?.height ?? window.innerHeight;
      setShellHeight(Math.max(200, vv - 96));
    };
    update();
    const vv = window.visualViewport;
    window.addEventListener('resize', update);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    return () => { if (boundsDebounceRef.current) clearTimeout(boundsDebounceRef.current); };
  }, []);

  const effectiveListView = isMobile ? 'grid' : listView;

  const filters = useMemo(() => paramsToFilters(searchParams), [searchParams]);
  const sort = searchParams.get('sort') || 'newest';
  const activeCount = countActiveFilters(filters);
  const searchKey = searchParams.toString();

  const [pendingJump, setPendingJump] = useState(null);

  // Consume lat/lng/zoom params injected by HeroSearch
  useEffect(() => {
    const latRaw = searchParams.get('lat');
    const lngRaw = searchParams.get('lng');
    if (!latRaw || !lngRaw) return;
    const lat = parseFloat(latRaw);
    const lng = parseFloat(lngRaw);
    const zoom = parseInt(searchParams.get('zoom'), 10) || 16;
    if (!isFinite(lat) || !isFinite(lng)) return;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('lat'); next.delete('lng'); next.delete('zoom');
      return next;
    }, { replace: true });

    lookupListingByAddress(lat, lng).then((match) => {
      if (match) {
        navigate(`/listing/${match.id}`);
      } else {
        setPendingJump({ lat, lng, zoom });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingJump || loading) return;
    const { lat, lng, zoom } = pendingJump;
    setPendingJump(null);
    requestAnimationFrame(() => mapRef.current?.zoomTo(lat, lng, zoom));
  }, [pendingJump, loading]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setPage(1);
    pageRef.current = 1;
    const fetchKey = ++fetchKeyRef.current;
    const fetchFilters = {
      ...filters,
      polygon: drawnPolygon || undefined,
      bounds: drawnPolygon
        ? undefined
        : (searchOnMove ? bounds : searchAreaBounds) || undefined,
    };
    currentFetchParamsRef.current = { filters: fetchFilters, sort };
    getAllListings({ filters: fetchFilters, sort, page: 1, pageSize: 50 })
      .then((res) => {
        if (fetchKeyRef.current !== fetchKey) return;
        setItems(res.items);
        setTotal(res.total);
        setNumPages(res.numPages);
        numPagesRef.current = res.numPages;
        setLoading(false);
      })
      .catch(() => {
        if (fetchKeyRef.current !== fetchKey) return;
        setItems([]);
        setTotal(null);
        setNumPages(1);
        numPagesRef.current = 1;
        setError(true);
        setLoading(false);
      });
  }, [searchKey, bounds, searchOnMove, drawnPolygon, searchAreaBounds, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      if (loadingMoreRef.current) return;
      if (pageRef.current >= numPagesRef.current) return;
      const params = currentFetchParamsRef.current;
      if (!params) return;
      const nextPage = pageRef.current + 1;
      const fetchKey = fetchKeyRef.current;
      loadingMoreRef.current = true;
      setLoadingMore(true);
      getAllListings({ ...params, page: nextPage, pageSize: 50 }).then((res) => {
        if (fetchKeyRef.current !== fetchKey) { loadingMoreRef.current = false; setLoadingMore(false); return; }
        setItems(prev => [...prev, ...res.items]);
        setPage(nextPage);
        pageRef.current = nextPage;
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }).catch(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
        toast.error('Could not load more homes. Please try again.');
      });
    }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchFilter = (patch) => {
    const params = filtersToParams({ ...filters, ...patch });
    if (sort !== 'newest') params.sort = sort;
    setSearchParams(params, { replace: true });
  };
  const setSort = (v) => { const p = filtersToParams(filters); if (v !== 'newest') p.sort = v; setSearchParams(p, { replace: true }); };

  const removeChip = (key, value) => {
    if (Array.isArray(key)) return patchFilter(Object.fromEntries(key.map((k) => [k, ''])));
    if (value != null) return patchFilter({ [key]: (filters[key] || []).filter((v) => v !== value) });
    patchFilter({ [key]: '' });
  };
  const clearAll = () => setSearchParams({}, { replace: true });

  const handleSelect = useCallback((id, listingObj) => {
    setSelectedId(id);
    setActiveId(id);
    setSelectedMapListing(listingObj ?? null);
    const el = listRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleBoundsChange = useCallback((newBounds) => {
    if (boundsEqual(lastBoundsRef.current, newBounds)) return;
    if (boundsDebounceRef.current) clearTimeout(boundsDebounceRef.current);
    boundsDebounceRef.current = setTimeout(() => {
      lastBoundsRef.current = newBounds;
      setBounds(newBounds);
    }, 300);
  }, []);

  const handleCloseCard = useCallback(() => {
    setSelectedId(null);
    setSelectedMapListing(null);
  }, []);

  const handlePolygonComplete = useCallback((ring) => {
    setDrawnPolygon(ring);
    setDrawMode(false);
  }, []);

  const autoLocate = !filters.zip && !filters.city && !filters.neighborhood;

  const handleAutoLocation = useCallback((area) => {
    mapRef.current?.setBoundaryCameraMode?.('draw-only');
    setSearchParams((prev) => {
      const current = paramsToFilters(prev);
      const patch = {};
      if (area.city) patch.city = area.city;
      // state intentionally omitted: the listings API does not reliably support
      // a bare `state` param (format mismatch / unsupported) causing empty results.
      // city or zip is precise enough; bounds-based search handles rural areas.
      const params = filtersToParams({ ...current, ...patch });
      const sortVal = prev.get('sort');
      if (sortVal && sortVal !== 'newest') params.sort = sortVal;
      return params;
    });
  }, [setSearchParams]);

  const handleRemovePolygon = useCallback(() => {
    setDrawnPolygon(null);
    mapRef.current?.clearPolygon();
  }, []);

  const handleDrawModeChange = useCallback((on) => {
    setDrawMode(on);
  }, []);

  const handleSearchArea = useCallback((b) => {
    setSearchAreaBounds(b);
  }, []);

  const selectedListing = items.find((l) => l.id === selectedId) || selectedMapListing || null;

  const handleFilterButtonClick = () => {
    if (window.innerWidth >= 768) {
      setFiltersDesktopOpen((o) => !o);
    } else {
      setFilterOpen(true);
    }
  };

  const handlePlaceSelect = useCallback(async (payload) => {
    const { viewport, location, isPreciseAddress, ...filterPatch } = payload;

    if (isPreciseAddress) {
      if (location) {
        const match = await lookupListingByAddress(location.lat, location.lng);
        if (match) {
          navigate(`/listing/${match.id}`);
          return;
        }
        mapRef.current?.zoomTo(location.lat, location.lng);
      }
      return;
    }

    // Infer city/ZIP when Places only returned `q`
    if (!filterPatch.zip && !filterPatch.city) {
      const text = String(filterPatch.q || '').trim();
      const zipM = text.match(/^(\d{5})(?:-\d{4})?$/);
      if (zipM) {
        filterPatch.zip = zipM[1];
        delete filterPatch.q;
      } else if (text) {
        const city = text.replace(/,\s*(FL|Florida)\s*$/i, '').split(',')[0].trim();
        if (city && !/^\d{5}/.test(city)) {
          filterPatch.city = city;
          delete filterPatch.q;
        }
      }
    }
    if (filterPatch.city) {
      filterPatch.city = String(filterPatch.city).replace(/,\s*(FL|Florida)\s*$/i, '').trim();
    }
    if (filterPatch.zip) {
      const zm = String(filterPatch.zip).match(/^(\d{5})/);
      if (zm) filterPatch.zip = zm[1];
    }

    const boundaryType = filterPatch.zip ? 'zip' : (filterPatch.city ? 'city' : null);
    const boundaryKey = filterPatch.zip || filterPatch.city || '';
    const hasLocFilter = !!(filterPatch.zip || filterPatch.city || filterPatch.neighborhood);
    if (hasLocFilter) mapRef.current?.setBoundaryCameraMode?.('fit');

    patchFilter({
      zip: '',
      city: '',
      neighborhood: '',
      streetName: '',
      q: '',
      ...filterPatch,
    });

    // Imperative paint — do not rely only on filter useEffect
    if (boundaryType && boundaryKey) {
      try {
        const geo = await getGeoBoundary({ type: boundaryType, key: boundaryKey });
        if (geo?.geojson) {
          mapRef.current?.paintBoundary?.(geo.geojson, geo.bbox || null, { fit: true });
          return;
        }
      } catch (_) { /* fall through to viewport */ }
    }

    if (!hasLocFilter) {
      if (viewport && mapRef.current) mapRef.current.fitBounds(viewport);
      else if (location && mapRef.current) mapRef.current.zoomTo(location.lat, location.lng);
    }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="flex flex-col md:h-[calc(100vh-6rem)]"
      style={isMobile && shellHeight != null ? { height: shellHeight } : undefined}
    >
      {/* Sticky sub-header bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
      <div className="px-4 py-2.5 flex flex-col gap-2 md:flex-row md:items-center">
        {/* Search bar — full width on mobile */}
        <PlacesSearchBar
          onSelect={handlePlaceSelect}
          className="w-full min-w-0 md:w-96 md:shrink-0"
        />

        {/* Filter + sale/rent mode (same row) */}
        <div className="flex items-center gap-2 shrink-0">
          <ListingTypeToggle
            value={getListingType(filters)}
            onChange={(type) => patchFilter({ listingType: [type] })}
          />
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <button
              onClick={handleFilterButtonClick}
              className={cn(
                'flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors shrink-0',
                filtersDesktopOpen
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              )}
              data-testid="map-open-filters-button"
            >
              <SlidersHorizontal size={15} />
              Filters{activeCount ? ` (${activeCount})` : ''}
            </button>
            <SheetContent side="left" className="w-[340px] overflow-y-auto md:hidden">
              <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
              <div className="mt-6"><FilterControls filters={filters} onChange={patchFilter} /></div>
              <Button className="mt-6 w-full rounded-xl" onClick={() => setFilterOpen(false)}>Apply</Button>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {activeCount > 0 && (
        <div className="px-4 pb-2 border-t border-slate-100">
          <AppliedChips filters={filters} onRemove={removeChip} onClearAll={clearAll} />
        </div>
      )}
      </div>

      {/* Mobile filter bar */}
      <div className="border-b border-slate-200 bg-white md:hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          {drawnPolygon && (
            <button
              onClick={handleRemovePolygon}
              className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
              data-testid="remove-polygon"
            >
              Boundary active<X size={13} className="ml-0.5" />
            </button>
          )}
          <span className="ml-auto text-sm text-slate-600" data-testid="map-results-count">{loading ? 'Loading…' : displayCount(items, total, page, numPages, filters)}</span>
          <SortSelect value={sort} onChange={setSort} />
        </div>
      </div>

      {/* Mobile view toggle */}
      <div className="flex md:hidden">
        <button onClick={() => setMobileView('map')} className={cn('flex-1 py-2 text-sm font-medium', mobileView === 'map' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600')} data-testid="mobile-view-map"><MapIcon size={15} className="mr-1 inline" />Map</button>
        <button onClick={() => setMobileView('list')} className={cn('flex-1 py-2 text-sm font-medium', mobileView === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600')} data-testid="mobile-view-list"><ListIcon size={15} className="mr-1 inline" />List</button>
      </div>

      {/* 3-column desktop layout */}
      <div className={cn(
        'grid grid-rows-[minmax(0,1fr)] flex-1 min-h-0 overflow-hidden',
        filtersDesktopOpen
          ? 'md:grid-cols-[280px_1fr_minmax(360px,40%)]'
          : 'md:grid-cols-[1fr_minmax(360px,40%)]'
      )}>
        {/* Left sidebar (desktop only, toggleable) */}
        {filtersDesktopOpen && (
          <div className="hidden min-h-0 border-r border-slate-200 md:flex">
            <FilterSidebar filters={filters} onChange={patchFilter} />
          </div>
        )}

        {/* Map (center) */}
        <div className={cn('relative h-full min-h-0', mobileView === 'list' && 'hidden md:block')}>
          <MapView
            ref={mapRef}
            listings={items}
            activeId={activeId}
            hoveredId={hoveredId}
            selectedListing={selectedListing}
            filters={filters}
            onSelect={handleSelect}
            onCloseCard={handleCloseCard}
            onBoundsChange={handleBoundsChange}
            searchOnMove={searchOnMove && !drawnPolygon}
            drawMode={drawMode}
            onPolygonComplete={handlePolygonComplete}
            drawnPolygon={drawnPolygon}
            onDrawModeChange={handleDrawModeChange}
            onSearchArea={handleSearchArea}
            onClearDrawnPolygon={handleRemovePolygon}
            focusKey={searchKey}
            autoLocate={autoLocate}
            onAutoLocation={handleAutoLocation}
          />
        </div>

        {/* List column (right) */}
        <div className={cn('flex min-h-0 flex-col bg-slate-50', mobileView === 'map' && 'hidden md:flex')} data-testid="map-list-column">
          {/* Header with count + controls (desktop only) */}
          <div className="hidden border-b border-slate-200 bg-white px-4 py-3 md:flex items-center gap-3">
            <span className="text-sm font-medium text-slate-900" data-testid="map-results-count-desktop">{loading ? 'Loading…' : displayCount(items, total, page, numPages, filters)}</span>
            <div className="ml-auto flex items-center gap-3">
              {drawnPolygon && (
                <button
                  onClick={handleRemovePolygon}
                  className="flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                  data-testid="remove-polygon-desktop"
                >
                  Boundary active<X size={12} className="ml-0.5" />
                </button>
              )}
              <div className="flex overflow-hidden rounded-xl border border-slate-200">
                <button
                  onClick={() => setListView('compare')}
                  className={cn(
                    'grid h-8 w-8 place-items-center transition-colors',
                    listView === 'compare' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  )}
                  aria-label="Compare view"
                  data-testid="map-view-compare"
                >
                  <Columns2 size={16} />
                </button>
                <button
                  onClick={() => setListView('grid')}
                  className={cn(
                    'grid h-8 w-8 place-items-center transition-colors border-l border-slate-200',
                    listView === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  )}
                  aria-label="Grid view"
                  data-testid="map-view-grid"
                >
                  <LayoutGrid size={16} />
                </button>
              </div>
              <SortSelect value={sort} onChange={setSort} />
            </div>
          </div>

          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}</div>
            ) : error ? (
              <EmptyState
                title="Could not load homes"
                description="Something went wrong while searching this area. Please try again."
                actionLabel="Try again"
                onAction={() => setRetryKey((k) => k + 1)}
              />
            ) : items.length === 0 && page >= numPages ? (
              <EmptyState actionLabel="Clear all filters" onAction={clearAll} />
            ) : items.length > 0 ? (
              effectiveListView === 'compare' ? (
                <div className="space-y-4">
                  <CompareHero listing={selectedListing || items[0]} stacked />
                  <AdSlot slotId="grid-native" size="native" />
                  <CompareTable items={items} selectedId={selectedId || items[0]?.id} onSelect={handleSelect} />
                  {loadingMore && (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={`more-${i}`} />)}
                    </div>
                  )}
                  {page < numPages && <div ref={sentinelRef} className="h-1" />}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {items.map((l, i) => (
                    <div key={l.id} className="contents">
                      <div
                        ref={(el) => (listRefs.current[l.id] = el)}
                        onMouseEnter={() => setHoveredId(l.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        className={cn('rounded-2xl transition-shadow', activeId === l.id && 'ring-2 ring-blue-500 ring-offset-2')}
                      >
                        <ListingCard listing={l} />
                      </div>
                      {((i + 1) % 4 === 0 || (items.length < 4 && i === items.length - 1)) && (
                        <AdSlot slotId="grid-native" size="native" />
                      )}
                    </div>
                  ))}
                  {loadingMore && (
                    <div className="sm:col-span-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={`more-${i}`} />)}
                    </div>
                  )}
                  {page < numPages && <div ref={sentinelRef} className="sm:col-span-2 h-1" />}
                </div>
              )
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
